import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getUserRoles } from "../../utils/helpers";
import { consoleForPath, entitledConsoles, landingPathFor } from "../constants/consoles";
import { savePreferredConsoleId } from "../constants/consolePreference";

// The one control for moving between dashboards, mounted in both topbars.
//
// It replaces three separate one-way buttons -- "OPS Console" and "Pharmacy Console" in
// the legacy topbar, "Legacy Console" in the v2 one. Those were pairwise links that
// could not show where you currently were: an admin saw two buttons and no indication
// of which console they were already in. A segmented control makes the full set and the
// current position visible at once, and scales past two consoles.
//
// One set of styles for both hosts: the legacy topbar and the v2 one already render the
// same elm-500 button language, so there is nothing to vary.
//
// Choosing a console also records it as this browser's preference, so the next login
// lands there.

const BASE = "px-3 py-2 font-sans text-sm font-semibold transition-colors";
const ACTIVE = "bg-elm-500 text-white-50";
const INACTIVE = "bg-transparent text-elm-500 hover:bg-elm-500 hover:text-white-50";

function ConsoleSwitcher() {
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const roles = getUserRoles();
    const consoles = entitledConsoles(roles);

    // Nothing to switch between -- a legacy-only role (MARKETING), an ops-only role
    // (KAM, which has no legacy pages at all), or a role with no dashboard. Rendering a
    // one-option "switcher" would just be a button that reloads the page you're on.
    if (consoles.length < 2) return null;

    const currentId = consoleForPath(pathname);

    return (
        <div
            className="flex border-2 border-elm-500 rounded-2xl overflow-hidden"
            role="group"
            aria-label="Switch dashboard"
        >
            {consoles.map((entry) => {
                const isActive = entry.id === currentId;
                return (
                    <button
                        key={entry.id}
                        type="button"
                        aria-current={isActive ? "page" : undefined}
                        title={isActive ? `You are in the ${entry.label}` : `Switch to the ${entry.label}`}
                        onClick={() => {
                            savePreferredConsoleId(entry.id);
                            if (!isActive) navigate(landingPathFor(entry.id, roles));
                        }}
                        className={`${BASE} ${isActive ? ACTIVE : INACTIVE}`}
                    >
                        {entry.label}
                    </button>
                );
            })}
        </div>
    );
}

export default ConsoleSwitcher;
